define([
    'jQuery',
    'underscore',
    'library/fcse-binding/binding',
    'library/fcse-compatibility/object',
    'library/fcse-binding/singlePropertyBinding',
    'library/fcse-binding/multiplePropertiesBinding',
    'library/fcse-events/eventDispatcher'],
    function($, _, Binding, Object, SinglePropertyBinding, MultiplePropertiesBinding, EventDispatcher) {
        /**
         * Base class for all Widgets
         *
         * @param element
         * @param options
         * @constructor
         */
        var Widget = function (element, options) {
            EventDispatcher.apply(this, arguments);
            
            // Component Instance
            if(element instanceof $) {
                this.element = element;
            } else {
                this.element = $(element);
            }
            
            /**
             * Current options for this Widget
             */
            var defaultOptions = {
                enabled: true
            };
            this.addOptions(defaultOptions, options);
            
            this.value = this._processData(this.element.data("initial-value"));
            
            /**
             * Stack of previous values - for undo functionality
             */
            this._valueStack = [];

            //Register events
            this.registerEvent("change");
            this.registerEvent("accept");
            this.registerEvent("cancel");   
            this.registerEvent("undo");
            this.registerEvent("validationError");
        };

        Widget.prototype = {
           /**
             * The root element in the DOM
             */
            element: null,
            
            /**
             * The internal value
             * Should always be accessed through getValue, setValue
             */
            value: null,
            
            /**
             * A value stack for old values (for history)
             */
            _valueStack: [],
            
            /**
             * Internal variable to store configuration values
             */
            _options: {},
            
            /**
             * Adds options to the widget. 
             * The parameter options will overwrite the defaults
             * @param {object} defaults
             * @param {object|undefined} options
             * @returns {undefined}
             */
            addOptions: function(defaults, options){
                this._options = $.extend({}, this.getOptions(), defaults, options || {});
            },
            
            /**
             * Returns an object of all configuration options of the widget
             * @returns {undefined|Object}
             */
            getOptions: function(){
                return this._options;
            },
            
            /**
             * Gets or sets option
             * @param name
             * @param value optional
             */
            option: function (name, value) {
                if (!_.isUndefined(value)) {
                    this._options[name] = value;
                }
                return this._options[name];
            },
            
            
            /**
             * Triggers event, both on element and if a handler was defined in options
             * @param eventType
             * @param data
             */
            _triggerEvent: function (eventType, data) {
                this.element.trigger(eventType, data);
                if (eventType in this._options && _.isFunction(this._options[eventType])) {
                    this._options[eventType].apply(this.element, data);
                }
            },
            /**
             * Bind this Widget to a scope
             * @param scope Scope
             * @param binding binding
             */
            bindToScope: function (scope, binding) {
                var self = this;
                if (typeof binding === "string") {
                    //shorthand for simple bindings is just scope parameter name
                    binding = new SinglePropertyBinding(binding);
                } else if ($.isPlainObject(binding)) {
                    //shorthand for multiple property bindings is mapping
                    binding = new MultiplePropertiesBinding(binding);
                }
                this._applyBinding(binding, scope);

                var toScopeInProgress = false;
                var fromScopeInProgress = false;
                //Widget -> scope changes
                this.element.on("change", function (e, source, value) {
                    if(!$(e.target).is(self.element)) {
                        return;
                    }
                    if(!fromScopeInProgress) {
                        toScopeInProgress = true;
                        self._applyBinding(binding, scope, value);
                        toScopeInProgress = false;
                    }
                });
                //scope -> Widget changes
                scope.paramChange(function (event, name, value) {
                    fromScopeInProgress = true;
                    //need to check if scope param change is triggered by Widget -> scope changes and ignore if it is
                    if (!toScopeInProgress && binding.isValueAffectedBy(name)) {
                        self.setValue(binding.fromScope(event.scope.getParams()));
                    }
                    fromScopeInProgress = false;
                });
            },
            /**
             * Gets internal value
             * @returns {*}
             */
            getValue: function () {
                // Defensive copy, so that previous value can be safely modified and be set again -- AG.
                if (_.isArray(this.value)) {
                    return this.value.slice();
                }
                else if (_.isObject(this.value)){
                    return $.extend(true, {}, this.value);
                }
                else {
                    return this.value;
                }
            },
            /**
             * Sets internal value
             * @param value
             */
            setValue: function (value) {
                if ((_.isObject(value) && !_.isEqual(this.value, value)) || this.value !== value) {
                    this._valueStack[this._valueStack.length] = this.value;
                    this.value = value;
                    this.setValueToUi();
                    this._triggerEvent("change", [this, this.value]);
                    this.dispatchEvent("change");
                }
            },
            
            /**
             * Undoes the latest value change
             */
            undo: function () {
                if (this._valueStack.length) {
                    this.value = this._valueStack.pop();
                    this.setValueToUi();
                }
                this.dispatchEvent("undo");
            },
            /**
             * Applies value from UI elements
             */
            accept: function () {
                this.setValue(this.getValueFromUi());
                this.dispatchEvent("accept");
            },
            /**
             * Cancels editing of the control - restores UI to the state that represents current control value
             */
            cancel: function () {
                this.setValueToUi();
                this.dispatchEvent("cancel");
            },
            /**
             * Updates UI elements with the currently selected value
             */
            setValueToUi: function () {
                //abstract, should be implemented in descendants
                throw "not implemented";
            },
            /**
             * Parses values from UI elements into internal value representation
             */
            getValueFromUi: function () {
                //abstract, should be implemented in descendants
                throw "not implemented";
            },
            
            /**
             * Return formatted value
             */
            formatValue: function () {
                return null;
            },

            /**
             * Validates the data in the widget.
             * Displaying error messages should be triggered here.
             *
             * @return boolean
             */
            isValid: function() {
                return true;
            },

            /**
             * Allow sub classes to precise data from initial structure if necessary
             * as well as to validate initial data.
             * Usually comes from attribute data-initial-value of DOM-Element
             */
            _processData: function (data) {
                return data;
            },
            _applyBinding: function(binding, scope, value) {
                // fyi: this.getValue() does not use DOM.
                var val = _.isUndefined(value) ? this.getValue() : value;
                if (!_.isUndefined(val)) {
                    scope.setMultipleParams(binding.toScope(val));
                }
            },
            init: function(scope) {

            }
        };
        
        //Add mixins
        Widget.prototype = $.extend(Object.create(Widget.prototype), Object.create(EventDispatcher.prototype));

        return Widget;
    }
);


//////////////////
// WEBPACK FOOTER
// ./~/fcse-widget/widget.js
// module id = 4
// module chunks = 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23